import { format, formatISO, parseISO } from "date-fns";
import { ImageType } from "duck";
import { Fragments } from "requests";

const filterImageOrNull = (value: number | ImageType | null) => {
  return typeof value !== "number" ? value || null : undefined;
};

export const parseStoredCase = (medCase: Fragments.StoredMedicalCase) => {
  const {
    client,
    createdAt,
    authorizations,
    disclaimerProcedures,
    name: caseName,
    status
  } = medCase;
  const {
    disclaimer: { _id: disclaimerId, signature },
    patient: {
      name,
      species,
      breed,
      dateOfBirth,
      sex,
      weight,
      weightMeasure,
      procedureDate,
      alerts,
      procedures,
      image,
      dropOff,
      vaccines,
      patientTests,
      treatment,
      examNote: { lastMealAt, ...examNote },
      currentMedications,
      preAnestheticExam,
      physicalExam,
      diagnosticTesting,
      medicationCalculations,
      miscellaneousGroup,
      defibMedications,
      fluidBolus,
      regionalBlocks,
      calculationAgreement,
      tablets,
      suspensions,
      tibialPlateauLevelingOsteotomyPlanning,
      generalAssessment,
      dentalChartings,
      dentalSketch,
      dentalSketchComments,
      monitoring,
      anestheticGases,
      fluidTemperatures,
      postAnestheticVitalComment,
      postAnestheticVitals,
      patientProcedures,
      vitalComment,
      vitals
    },
    ...clientInfo
  } = client;

  return {
    name: caseName,
    status,
    createdAt: formatISO(new Date(createdAt)),
    authorizations,
    disclaimerProcedures,
    client: {
      ...clientInfo,
      disclaimer: {
        ...(typeof disclaimerId === "number" ? { _id: disclaimerId } : {}),
        signature:
          (signature as any)?.id || typeof signature === "number"
            ? undefined
            : (signature as any)
      },
      patient: {
        name,
        species,
        breed,
        dateOfBirth: dateOfBirth
          ? format(new Date(dateOfBirth), "MM/dd/y")
          : null,
        sex,
        weight,
        weightMeasure,
        procedureDate: procedureDate
          ? format(parseISO(procedureDate), "MM/dd/y")
          : null,
        alerts,
        procedures: procedures || [],
        image: (image as any)?.base64Format ? (image as ImageType) : null,
        dropOff,
        vaccines,
        patientTests,
        monitoring: {
          _id: monitoring._id,
          monitoringBy: monitoring.monitoringBy?._id
        },
        anestheticGases,
        fluidTemperatures,
        postAnestheticVitalComment,
        postAnestheticVitals,
        vitalComment,
        vitals,
        patientProcedures: patientProcedures.map(
          ({ startTime, endTime, ...proc }) => ({
            startTime: startTime ? formatISO(new Date(startTime)) : null,
            endTime: endTime ? formatISO(new Date(endTime)) : null,
            ...proc
          })
        ),
        treatment,
        examNote: {
          lastMealAt: lastMealAt ? formatISO(new Date(lastMealAt)) : null,
          ...examNote
        },
        preAnestheticExam,
        physicalExam,
        diagnosticTesting,
        medicationCalculations: medicationCalculations.map(calc => {
          const { totalVolume, totalAmount, ...data } = calc;
          return data;
        }),
        miscellaneousGroup,
        defibMedications,
        fluidBolus,
        regionalBlocks,
        ...(calculationAgreement
          ? {
              calculationAgreement:
                typeof calculationAgreement?.file === "number"
                  ? undefined
                  : (calculationAgreement as any)
            }
          : {}),
        tablets,
        suspensions,
        tibialPlateauLevelingOsteotomyPlanning,
        ...(generalAssessment
          ? {
              generalAssessment: {
                ...generalAssessment,
                beforePhotoRight: filterImageOrNull(
                  generalAssessment.beforePhotoRight
                ),
                beforePhotoFront: filterImageOrNull(
                  generalAssessment.beforePhotoFront
                ),
                beforePhotoLeft: filterImageOrNull(
                  generalAssessment.beforePhotoLeft
                ),
                afterPhotoRight: filterImageOrNull(
                  generalAssessment.afterPhotoRight
                ),
                afterPhotoFront: filterImageOrNull(
                  generalAssessment.afterPhotoFront
                ),
                afterPhotoLeft: filterImageOrNull(
                  generalAssessment.afterPhotoLeft
                ),
                additionalPhotos: generalAssessment.additionalPhotos.filter(
                  photo => typeof photo !== "number"
                ) as ImageType[],
                radiographicFindingsPhotos: generalAssessment.radiographicFindingsPhotos.filter(
                  photo => typeof photo !== "number"
                ) as ImageType[]
              }
            }
          : {}),
        ...(dentalChartings
          ? {
              dentalChartings: dentalChartings.map(({ _id, ...chart }) => chart)
            }
          : {}),
        dentalSketch: (dentalSketch as any)?.base64Format
          ? (dentalSketch as ImageType)
          : null,
        dentalSketchComments: dentalSketchComments.map(
          ({ createdBy, ...comment }) => comment
        ),
        currentMedications: currentMedications.map(
          ({ lastGivenAt, ...med }) => ({
            ...med,
            lastGivenAt: formatISO(new Date(lastGivenAt))
          })
        )
      }
    }
  };
};
